
import { Vue, Component, Prop , Watch} from "vue-property-decorator";
import { makeNameQuery } from "@/helpers/queryHelperFunctions";
import GET_PROJECT_CONTRACTORS from "@/apollo/projects/GetProjectContractors.gql";
import { cloneDeep, isEqual } from "lodash-es";
import {
  getPaginationDataFromQuery,
  getPaginationQuery,
} from "@/helpers/router/preparePaginationQuery";
import EmptyListPlaceholder from "@/components/common/EmptyListPlaceholder.vue";
import CardPagination from "@/components/card/PaginationNew.vue";
import CardBodyItem from "@/components/card/CardBodyItemNew.vue";
import {
  getFiltersDataFromQuery,
  joinQueryValues,
} from "@/helpers/router/prepareFiltersQuery";
import ContractorCollectionFilters from "@/components/companyContractors/ContractorCollectionFiltersProjects.vue";
import { Contractor, Project, User, User_Profile, Contractor_Bool_Exp,
  User_Profile_Bool_Exp, } from "@/generated/graphql";
  import getStateQueryConditions from "@/helpers/locations/stateQueryConditions";
import ContractorsCollectionFiltersQuery from "@/entities/filters/ContractorsCollectionFiltersQueryProject";
import PaginationConfig from "@/entities/PaginationConfig";
import paginationConfig from "@/data/paginationConfig";
import ContractorRating from "@/components/rating/ContractorRatingNew.vue";
import ContractorCard from "@/components/companyContractors/ContractorCardRequest.vue";
import getPaginationOffset from "@/helpers/pagination/getPaginationOffset";
import LoadingOverlay from "@/components/common/LoadingOverlay.vue";
import Roles from "@/entities/Roles";
import contractorsCollectionFiltersTemplate from "@/data/filtersTemplates/contractorsCollectionFiltersTemplateProjects";
import RouteNames from "@/entities/RouteNames";
import { NO_INFORMATION } from "@/data/placeholders";
import EmptyListImageContractors from "@/components/icons/emptyListImages/EmptyListImageContractors.vue";

interface ContractorWithActivities extends Contractor {
  activities: number;
}

@Component({
  components: {
    EmptyListImageContractors,
    LoadingOverlay,
    ContractorCard,
    ContractorRating,
    EmptyListPlaceholder,
    ContractorCollectionFilters,
    CardPagination,
    CardBodyItem,
  },
  apollo: {
    contractors: {
      query: GET_PROJECT_CONTRACTORS,
      variables() {
        return this.directoryFilter;
      },
      update(data) {
        this.paginationConfig.total = data.total.aggregate.count;
        this.paginationConfig.loadedCount = data.contractor.length;
        return data.contractor;
      },
    },
  },
})
export default class Contractors extends Vue {
  @Prop({ type: Object, required: true }) readonly project!: Project;
  @Prop({ type: Object, required: true }) readonly currentUser!: User;
  @Prop({ type: String, required: true }) readonly companyId!: string;


  get directoryFilter(): any {
    const { page, limit } = this.paginationConfig;

    const {
      countries,
      states,
      cities,
      titles,
      skills,
      name,
      available,
      minRate,
      maxRate,
      managers,
    } = this.directoryFilters;

    var filterConditions = [];

    if (name) {
      filterConditions.push(makeNameQuery(name, ["user"]));
    }

    if (countries.length) {
      filterConditions.push({
        user: {
          _or: [
            { profile: { country: { _in: countries } } },
            { user_profile_by_managements: { country: { _in: countries } } },
          ],
        },
      });
    }

    if (states.length) {
      filterConditions.push({
        user: {
          _or: [
            {
              profile: {
                state: getStateQueryConditions({ countries, states }),
              },
            },
            {
              user_profile_by_managements: {
                state: getStateQueryConditions({ countries, states }),
              },
            },
          ],
        },
      });
    }

    if (cities.length) {
      filterConditions.push({
        user: {
          _or: [
            {
              profile: {
                city: { _in: cities },
              },
            },
            { user_profile_by_managements: { city: { _in: cities } } },
          ],
        },
      });
    }

    if (available !== "all") {
      filterConditions.push({
        user: {
          available: { _eq: available === "yes" },
        },
      });
    }

    if (titles.length) {
      filterConditions.push({
        user: {
          _or: [
            {
              profile: {
                job_title: { _in: titles },
              },
            },
            {
              experiences: {
                job_title: {
                  _in: titles,
                },
              },
            },
            { user_profile_by_managements: { job_title: { _in: titles } } },
          ],
        },
      });
    }

    if (skills.length) {
      filterConditions.push({
        user: {
          profile: {
            skills: {
              skill_id: { _in: skills },
            },
          },
        },
      });
    }

    if (managers.length) {
      filterConditions.push({
        managers: {
          _and: [
            { manager_id: { _in: managers } },
            { approved: { _eq: true } },
          ],
        },
      });
    }
    
    // if (projects.length) {
    //   filterConditions.push({
    //     project_activities: {
    //       project: {
    //         id: { _in: projects },
    //       },
    //     },
    //   });
    // }

    if (minRate || maxRate) {
      const minConditions: User_Profile_Bool_Exp[] = [];
      const maxConditions: User_Profile_Bool_Exp[] = [];

      if (minRate) {
        minConditions.push({ min_rate: { _gte: minRate } });
        maxConditions.push({ max_rate: { _gte: minRate } });
      }
      if (maxRate) {
        minConditions.push({ min_rate: { _lte: maxRate } });
        maxConditions.push({ max_rate: { _lte: maxRate } });
      }

      if (minConditions.length && maxConditions.length) {
        filterConditions.push({
          user: {
            _or: [
              {
                profile: {
                  _or: [{ _and: minConditions }, { _and: maxConditions }],
                },
              },
              {
                user_profile_by_managements: {
                  _or: [{ _and: minConditions }, { _and: maxConditions }],
                },
              },
            ],
          },
        });
      }
    }

    return {
      offset: getPaginationOffset({ page, limit }),
      limit: limit,
      where: {
      _and: filterConditions,
        project_activities: {
              project_id: {
                _eq: this.$route.params.id,
              },
            },
          },
      id: this.currentUser.id,
  }
}

getOccupation(contractor): String | null {
 if (
			contractor &&
			contractor.user &&
      contractor.user?.profile?.job_title
			
		) {
      return contractor.user?.profile
				?.job_title;
			
		} else if (
			contractor.user.experiences[0]
				?.job_title
		) {
			return contractor.user?.experiences[0]
				.job_title;
		} else {
			return;
		}
	}

resetFilters(): void {
    if (isEqual(this.directoryFilters, contractorsCollectionFiltersTemplate)) {
      return;
    }

    this.directoryFilters = cloneDeep(contractorsCollectionFiltersTemplate);
    this.paginationConfig = { ...paginationConfig };
  }

 updateFilters(updatedFilters: ContractorsCollectionFiltersQuery): void {
    const dataEqual = isEqual(this.directoryFilters, updatedFilters);

    if (dataEqual) {
      return;
    }

    const { minRate, maxRate, ...filtersData } = updatedFilters;


    const query = joinQueryValues({
      ...filtersData,
      min_rate: minRate,
      max_rate: maxRate,
    });

    this.$router.replace({ query }).catch(() => {});
  }

  get queryPropsToWatch(): string {
    return JSON.stringify([this.directoryFilters, this.paginationConfig]);
  }

  get queryParams(): {
    filters: ContractorsCollectionFiltersQuery;
    pagination: PaginationConfig;
  } {
    const query = this.$route.query;
    const { min_rate: minRate, max_rate: maxRate, ...queryParams } = query;

    const queryData = {
      minRate,
      maxRate,
      ...queryParams,
    };

    return {
      filters: getFiltersDataFromQuery<ContractorsCollectionFiltersQuery>(
        contractorsCollectionFiltersTemplate,
        queryData
      ),
      pagination: {
        ...this.paginationConfig,
        ...getPaginationDataFromQuery(query),
      },
    };
  }

  get isChosenPageEmpty(): boolean {
    const collectionEmpty = this.contractors?.length === 0;
    const notFirstPage = this.paginationConfig.page !== 1;

    return !this.loading && collectionEmpty && notFirstPage;
  }

  showFirstPage(): void {
    this.paginationConfig = {
      ...this.paginationConfig,
      page: paginationConfig.page,
    };
  }

  @Watch("queryPropsToWatch")
    updateQuery(): void {
    if (this.isChosenPageEmpty) {
      return this.showFirstPage();
    }

    const paginationQuery = getPaginationQuery(this.paginationConfig);

    const { minRate, maxRate, ...filtersData } = this.directoryFilters;

    const query = joinQueryValues({
      ...paginationQuery,
      ...filtersData,
      min_rate: minRate,
      max_rate: maxRate,
    });

    if (isEqual(query, this.$route.query)) {
      return;
    }

    this.$router.push({ query }).catch(() => {});
  }

  @Watch("$route.query", { immediate: true })
      onQueryChange(): void {
    const { filters, pagination } = this.queryParams;

    this.directoryFilters = filters;
    this.paginationConfig = pagination;
  }
  
  paginationConfig: PaginationConfig = { ...paginationConfig };
  contractors: Contractor[] | null = null;

  get filtersEmpty(): boolean {
    return isEqual(this.directoryFilters, contractorsCollectionFiltersTemplate);
  }

  directoryFilters: ContractorsCollectionFiltersQuery = cloneDeep(
    contractorsCollectionFiltersTemplate
  );

  get emptyListTitle(): string {
     if (this.filtersEmpty) {
         return "The client does not have any resources yet.";
    }
         return "No Resources for this selection.";
  }

  get isEmpty(): boolean {
    return this.contractors?.length === 0;
  }

  get currentUserManagerId(): string | undefined {
    return this.currentUser?.role === Roles.MANAGER
      ? this.currentUser.manager?.id
      : undefined;
  }

  message(userId: string): void {
    this.$router.push({
      name: RouteNames.INBOX,
      query: {
        recipientUserId: userId,
      },
    });
  }

  get loading(): boolean {
    return this.$apollo.queries.contractors.loading;
  }

   handleRefetch(): void {
    this.$apollo.queries.contractors.refetch();
    this.$emit("refetchRequests");
    this.$emit("refetchRequestsContractorsNumber");
  }

  prepareFilters(filterValues: Record<string, any>): void {
    this.directoryFilters = {
      ...this.directoryFilters,
      ...filterValues,
    };
  }

  getLocation(profile: User_Profile): string {
    if (!profile) {
      return NO_INFORMATION;
    }
    const location = [profile.city, profile.country]
      .filter((place) => Boolean(place))
      .join(" ");
    return location || NO_INFORMATION;
  }

  get isManager(): boolean {
    return this.currentUser.role === Roles.MANAGER;
  }

  isManagerAccessApproved(contractor: Contractor): boolean {
    return Boolean(contractor.managers[0]?.approved);
  }
}
